import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { paidAuditService } from '../../services/paidAuditService';
import { invoiceGenerator } from '../../utils/invoiceGenerator';
import { Helmet } from 'react-helmet';
import toast from 'react-hot-toast';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import AnnouncementBanner from '../../components/ui/AnnouncementBanner';
import Icon from '../../components/AppIcon';
import AuditTimeline from './components/AuditTimeline';
import TransactionDetails from './components/TransactionDetails';

const GRAD        = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';
const GRAD_SHADOW = '0 4px 16px rgba(0,74,173,0.35)';

/* ─── Styles ─────────────────────────────────────────────────────────────── */
const STYLES = `
  .pcs-root * { box-sizing: border-box; }
  .pcs-root   { font-family: inherit; min-height: 100vh; background: var(--background); }

  /* ── loading / error states ── */
  .pcs-center {
    display: flex; align-items: center; justify-content: center; padding-top: 120px;
    text-align: center;
  }
  .pcs-center p { font-size: 16px; color: var(--muted-foreground, #64748b); margin-top: 16px; }

  .pcs-error-wrap { max-width: 640px; margin: 80px auto; padding: 0 24px; }
  .pcs-error-card {
    background: var(--card, #fff);
    border: 1px solid var(--border, #e2e8f0);
    border-radius: 16px; padding: 32px;
  }
  .pcs-error-alert {
    display: flex; align-items: center; gap: 10px;
    background: rgba(220,38,38,0.08); border: 1px solid rgba(220,38,38,0.20);
    border-radius: 10px; padding: 12px 16px; margin-bottom: 24px;
  }
  .pcs-error-alert span { font-size: 13px; color: #dc2626; font-weight: 500; }

  /* ── main layout ── */
  .pcs-main  { padding-top: 58px; font-family: inherit; }
  .pcs-inner { max-width: 1200px; margin: 0 auto; padding: 32px 24px 64px; }

  /* ── success header ── */
  .pcs-hero { text-align: center; margin-bottom: 36px; }
  .pcs-hero-icon {
    display: inline-flex; align-items: center; justify-content: center;
    width: 88px; height: 88px; border-radius: 50%;
    background: rgba(22,163,74,0.10); margin-bottom: 20px;
  }
  .pcs-hero h1 {
    font-family: 'Sansation', system-ui, sans-serif;
    font-size: clamp(24px, 5vw, 36px);
    font-weight: 700; color: var(--foreground, #0f172a);
    margin: 0 0 10px; letter-spacing: -0.02em;
  }
  .pcs-hero p {
    font-size: 16px; color: var(--muted-foreground, #64748b);
    margin: 0 auto; max-width: 520px; line-height: 1.6;
  }

  /* ── two-column grid → single column on mobile ── */
  .pcs-grid {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 24px;
  }
  .pcs-left  { display: flex; flex-direction: column; gap: 24px; }
  .pcs-right { display: flex; flex-direction: column; gap: 24px; }

  /* ── shared card ── */
  .pcs-card {
    background: var(--card, #fff);
    border: 1px solid var(--border, #e2e8f0);
    border-radius: 16px; padding: 28px 28px 32px;
  }
  .pcs-card-title {
    font-family: 'Sansation', system-ui, sans-serif;
    font-size: 20px; font-weight: 700;
    color: var(--foreground, #0f172a); margin: 0;
  }
  .pcs-card-title-row {
    display: flex; align-items: center; gap: 10px; margin-bottom: 24px;
  }

  /* ── order summary rows ── */
  .pcs-row {
    padding-bottom: 16px; margin-bottom: 16px;
    border-bottom: 1px solid var(--border, #e2e8f0);
  }
  .pcs-row-flex {
    display: flex; justify-content: space-between; align-items: center;
    padding-bottom: 16px; margin-bottom: 16px;
    border-bottom: 1px solid var(--border, #e2e8f0);
    gap: 12px; flex-wrap: wrap;
  }
  .pcs-meta-label {
    font-size: 11px; color: var(--muted-foreground, #64748b);
    margin: 0 0 3px; text-transform: uppercase;
    letter-spacing: 0.06em; font-weight: 600;
  }
  .pcs-meta-value {
    font-size: 14px; font-weight: 600;
    color: var(--foreground, #0f172a); margin: 0;
  }
  .pcs-meta-sub { font-size: 12px; color: var(--muted-foreground, #64748b); margin: 3px 0 0; }
  .pcs-order-id {
    font-family: monospace; font-size: 15px;
    font-weight: 700; color: var(--foreground, #0f172a); margin: 0;
  }
  .pcs-paid-badge {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 5px 12px; border-radius: 100px;
    background: rgba(22,163,74,0.10); color: #16a34a;
    border: 1px solid rgba(22,163,74,0.25);
    font-size: 12px; font-weight: 700;
    white-space: nowrap; flex-shrink: 0;
  }
  .pcs-total-row {
    display: flex; justify-content: space-between;
    align-items: center; padding-top: 4px; gap: 12px; flex-wrap: wrap;
  }
  .pcs-total-label { font-size: 15px; font-weight: 700; color: var(--foreground, #0f172a); margin: 0; }
  .pcs-total-amount {
    font-family: 'Sansation', system-ui, sans-serif;
    font-size: clamp(22px, 4vw, 28px);
    font-weight: 700; color: #004AAD; margin: 0; letter-spacing: -0.02em;
  }

  /* ── invoice buttons ── */
  .pcs-invoice-btns { display: flex; gap: 12px; flex-wrap: wrap; }
  .pcs-btn-grad {
    flex: 1; min-width: 140px; padding: 13px 0; border-radius: 100px;
    border: none; background: linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%); color: #fff;
    font-size: 14px; font-weight: 700; cursor: pointer;
    font-family: inherit; box-shadow: ${GRAD_SHADOW};
    display: flex; align-items: center; justify-content: center; gap: 8px;
    transition: filter .15s, transform .15s;
  }
  .pcs-btn-grad:hover { filter: brightness(1.08); transform: translateY(-1px); }

  .pcs-btn-outline {
    flex: 1; min-width: 140px; padding: 13px 0; border-radius: 100px;
    border: 1px solid var(--border, #e2e8f0);
    background: transparent; color: var(--foreground, #0f172a);
    font-size: 14px; font-weight: 600; cursor: pointer;
    font-family: inherit;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    transition: border-color .15s;
  }
  .pcs-btn-outline:hover { border-color: #004AAD; }

  /* ── right-column cards ── */
  .pcs-email-card {
    background: rgba(0,74,173,0.04);
    border: 1px solid rgba(0,74,173,0.18);
    border-radius: 16px; padding: 22px 22px 24px;
  }
  .pcs-email-inner { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 14px; }
  .pcs-email-inner h3 {
    font-family: 'Sansation', system-ui, sans-serif;
    font-size: 16px; font-weight: 700;
    color: var(--foreground, #0f172a); margin: 0 0 3px;
  }
  .pcs-email-inner .sub  { font-size: 12px; color: var(--muted-foreground, #64748b); margin: 0 0 4px; }
  .pcs-email-inner .addr { font-size: 13px; font-weight: 600; color: var(--foreground, #0f172a); margin: 0; }
  .pcs-email-note {
    background: rgba(255,255,255,0.6); border-radius: 10px; padding: 10px 12px;
    font-size: 12px; color: var(--muted-foreground, #64748b); margin: 0; line-height: 1.6;
  }

  .pcs-actions-card {
    background: var(--card, #fff);
    border: 1px solid var(--border, #e2e8f0);
    border-radius: 16px; padding: 22px 22px 24px;
  }
  .pcs-actions-card h3 {
    font-family: 'Sansation', system-ui, sans-serif;
    font-size: 16px; font-weight: 700;
    color: var(--foreground, #0f172a); margin: 0 0 16px;
  }
  .pcs-actions-list { display: flex; flex-direction: column; gap: 10px; }

  .pcs-btn-grad-sm {
    width: 100%; padding: 12px 0; border-radius: 100px;
    border: none; background: linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%); color: #fff;
    font-size: 13px; font-weight: 700; cursor: pointer;
    font-family: inherit; box-shadow: ${GRAD_SHADOW};
    display: flex; align-items: center; justify-content: center; gap: 8px;
    transition: filter .15s, transform .15s;
  }
  .pcs-btn-grad-sm:hover { filter: brightness(1.08); transform: translateY(-1px); }

  .pcs-btn-outline-sm {
    width: 100%; padding: 12px 0; border-radius: 100px;
    border: 1px solid var(--border, #e2e8f0);
    background: transparent; color: var(--foreground, #0f172a);
    font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    transition: border-color .15s;
  }
  .pcs-btn-outline-sm:hover { border-color: #004AAD; }

  .pcs-btn-error {
    width: 100%; padding: 13px 0; border-radius: 100px;
    border: none; background: linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%); color: #fff;
    font-size: 14px; font-weight: 700; cursor: pointer;
    font-family: inherit; box-shadow: ${GRAD_SHADOW};
  }

  .pcs-trust-card {
    background: var(--muted, #f8fafc); border-radius: 16px; padding: 20px 22px;
  }
  .pcs-trust-card h3 {
    font-family: 'Sansation', system-ui, sans-serif;
    font-size: 14px; font-weight: 700;
    color: var(--foreground, #0f172a); margin: 0;
  }
  .pcs-trust-row { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
  .pcs-trust-body { font-size: 12px; color: var(--muted-foreground, #64748b); margin: 0 0 12px; line-height: 1.6; }
  .pcs-trust-support { display: flex; align-items: center; gap: 7px; }
  .pcs-trust-support span { font-size: 12px; color: var(--muted-foreground, #64748b); }

  /* ─────────── REFUND BANNER ─────────── */
  .pcs-refund {
    background: rgba(251,191,36,0.06);
    border: 1px solid rgba(251,191,36,0.30);
    border-radius: 14px; margin-bottom: 28px;
    overflow: hidden; font-family: inherit;
  }
  .pcs-refund-header {
    padding: 14px 20px; cursor: pointer;
    display: flex; align-items: center; justify-content: space-between;
  }
  .pcs-refund-header.open { border-bottom: 1px solid rgba(251,191,36,0.20); }
  .pcs-refund-header-left { display: flex; align-items: center; gap: 10px; }
  .pcs-refund-header-left .title { margin: 0; font-size: 13px; font-weight: 700; color: #92400e; }
  .pcs-refund-header-left .sub   { margin: 2px 0 0; font-size: 12px; color: #b45309; line-height: 1.5; }
  .pcs-refund-chevron { flex-shrink: 0; transition: transform .2s; }
  .pcs-refund-chevron.open { transform: rotate(180deg); }

  .pcs-refund-body { padding: 16px 20px 20px; }
  .pcs-refund-summary {
    background: #fffbeb; border: 1px solid rgba(251,191,36,0.25);
    border-radius: 10px; padding: 12px 16px; margin-bottom: 16px;
  }
  .pcs-refund-summary-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px;
  }
  .pcs-refund-summary .r-label {
    margin: 0; font-size: 10px; font-weight: 600; color: #92400e;
    text-transform: uppercase; letter-spacing: .05em;
  }
  .pcs-refund-summary .r-val {
    margin: 2px 0 0; font-size: 12px; font-weight: 600; color: #1d1d1f; word-break: break-all;
  }
  .pcs-refund-summary .r-val.mono { font-family: monospace; }

  .pcs-refund-textarea-label {
    display: block; font-size: 12px; font-weight: 600;
    color: #374151; margin-bottom: 6px;
  }
  .pcs-refund-textarea {
    width: 100%; padding: 10px 12px; border: 1px solid #e2e8f0;
    border-radius: 8px; font-size: 13px; color: #1d1d1f;
    font-family: inherit; resize: vertical; outline: none;
    transition: border-color .15s;
  }
  .pcs-refund-textarea:focus { border-color: #004AAD; }
  .pcs-refund-note { margin: 0 0 14px; font-size: 11px; color: #6b7280; line-height: 1.6; }

  .pcs-refund-actions { display: flex; gap: 10px; flex-wrap: wrap; }
  .pcs-refund-submit {
    flex: 1; min-width: 160px; padding: 10px 16px; border-radius: 9px;
    border: none; font-size: 13px; font-weight: 700;
    cursor: pointer; font-family: inherit; transition: all .15s;
  }
  .pcs-refund-submit.active   { background: linear-gradient(135deg,#dc2626,#b91c1c); color: #fff; }
  .pcs-refund-submit.disabled { background: #e2e8f0; color: #94a3b8; cursor: not-allowed; }
  .pcs-refund-dismiss {
    padding: 10px 16px; border-radius: 9px;
    border: 1px solid #e2e8f0; background: transparent;
    color: #6e6e73; font-size: 12px; font-weight: 600;
    cursor: pointer; font-family: inherit;
  }

  .pcs-refund-success {
    background: rgba(22,163,74,0.06); border: 1px solid rgba(22,163,74,0.20);
    border-radius: 14px; padding: 16px 20px;
    display: flex; align-items: center; gap: 12; margin-bottom: 24px;
  }
  .pcs-refund-success .title { margin: 0; font-size: 13px; font-weight: 700; color: #15803d; }
  .pcs-refund-success .body  { margin: 2px 0 0; font-size: 12px; color: #166534; line-height: 1.6; }

  /* ── MOBILE ── */
  @media (max-width: 768px) {
    .pcs-inner { padding: 24px 16px 48px; }

    .pcs-grid {
      grid-template-columns: 1fr;   /* stack left then right */
    }

    /* on mobile, right column flows naturally below left */

    .pcs-hero { margin-bottom: 24px; }
    .pcs-hero-icon { width: 72px; height: 72px; }
    .pcs-hero p { font-size: 14px; }

    .pcs-card { padding: 20px 16px 24px; border-radius: 14px; }
    .pcs-card-title { font-size: 17px; }

    .pcs-invoice-btns { flex-direction: column; }
    .pcs-btn-grad, .pcs-btn-outline { min-width: unset; }

    .pcs-refund-summary-grid { grid-template-columns: 1fr; gap: 8px; }
    .pcs-refund-actions { flex-direction: column; }
    .pcs-refund-submit { min-width: unset; }

    .pcs-email-card, .pcs-actions-card, .pcs-trust-card { border-radius: 14px; }
  }

  @media (max-width: 400px) {
    .pcs-inner { padding: 16px 12px 40px; }
    .pcs-card  { padding: 16px 12px 20px; }
  }
`;

/* ─── Refund Request Banner ───────────────────────────────────────────────── */
const RefundRequestBanner = ({ paymentOrder, onDismiss }) => {
  const [isOpen,      setIsOpen]      = useState(false);
  const [reason,      setReason]      = useState('');
  const [isSending,   setIsSending]   = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const sym    = paymentOrder?.currency === 'INR' ? '₹' : '$';
  const amount = (paymentOrder?.amount / 100)?.toFixed(2);

  const handleSubmit = async () => {
    if (!reason.trim()) { toast.error('Please describe why you need a refund'); return; }
    setIsSending(true);
    try {
      const subject = encodeURIComponent(`Refund Request - ${paymentOrder?.razorpay_payment_id}`);
      const body    = encodeURIComponent(
        `Payment ID: ${paymentOrder?.razorpay_payment_id}\n` +
        `Order ID: ${paymentOrder?.razorpay_order_id}\n` +
        `Amount: ${sym}${amount}\n` +
        `Customer: ${paymentOrder?.customer_name} (${paymentOrder?.customer_email})\n` +
        `Reason: ${reason}\n\nPlease process refund to original payment method.`
      );
      window.location.href = `mailto:support@mydeckaudit.com?subject=${subject}&body=${body}`;
      setIsSubmitted(true);
      toast.success('Refund request sent to support team');
    } catch {
      toast.error('Failed to send. Please email support@mydeckaudit.com directly.');
    } finally {
      setIsSending(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="pcs-refund-success" style={{ gap: 12, marginBottom: 24 }}>
        <span style={{ fontSize: 20 }}>✅</span>
        <div>
          <p className="title">Refund request sent</p>
          <p className="body">Our support team will process your refund within 2–3 business days. You'll receive an email confirmation shortly.</p>
        </div>
      </div>
    );
  }

  const summaryRows = [
    ['Payment ID', paymentOrder?.razorpay_payment_id || 'N/A', true],
    ['Amount',     `${sym}${amount}`,                          false],
    ['Email',      paymentOrder?.customer_email,               false],
    ['Date',       new Date(paymentOrder?.paid_at)?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }), false],
  ];

  return (
    <div className="pcs-refund">
      <div className={`pcs-refund-header${isOpen ? ' open' : ''}`} onClick={() => setIsOpen(o => !o)}>
        <div className="pcs-refund-header-left">
          <span style={{ fontSize: 18 }}>⚠️</span>
          <div>
            <p className="title">Did you try to cancel this payment?</p>
            <p className="sub">If you cancelled inside the payment screen, your money may still have been debited. You can request an instant refund here.</p>
          </div>
        </div>
        <svg className={`pcs-refund-chevron${isOpen ? ' open' : ''}`} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2.5" strokeLinecap="round">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </div>

      {isOpen && (
        <div className="pcs-refund-body">
          <div className="pcs-refund-summary">
            <div className="pcs-refund-summary-grid">
              {summaryRows.map(([label, value, mono]) => (
                <div key={label}>
                  <p className="r-label">{label}</p>
                  <p className={`r-val${mono ? ' mono' : ''}`}>{value}</p>
                </div>
              ))}
            </div>
          </div>

          <label className="pcs-refund-textarea-label">
            Reason for refund <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <textarea
            className="pcs-refund-textarea"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. I tried to cancel the UPI payment but it still went through..."
            rows={3}
          />

          <p className="pcs-refund-note" style={{ margin: '10px 0 14px' }}>
            💬 Refunds are processed within <strong>5–7 business days</strong> to your original payment method. Our team reviews every request within 24 hours.
          </p>

          <div className="pcs-refund-actions">
            <button
              className={`pcs-refund-submit${!isSending && reason.trim() ? ' active' : ' disabled'}`}
              onClick={handleSubmit}
              disabled={isSending || !reason.trim()}
            >
              {isSending ? 'Sending...' : '📩 Submit Refund Request'}
            </button>
            <button className="pcs-refund-dismiss" onClick={() => { setIsOpen(false); onDismiss?.(); }}>
              No, keep it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Main Screen ─────────────────────────────────────────────────────────── */
const PaymentConfirmationScreen = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, userProfile } = useAuth();

  const [paymentOrder,     setPaymentOrder]     = useState(null);
  const [isLoading,        setIsLoading]        = useState(true);
  const [error,            setError]            = useState(null);
  const [showRefundBanner, setShowRefundBanner] = useState(true);

  useEffect(() => {
    const loadPaymentOrder = async () => {
      try {
        const orderId = location?.state?.paymentOrderId || new URLSearchParams(location?.search)?.get('orderId');
        if (!orderId) { setError('No payment order found'); setIsLoading(false); return; }
        const orders = await paidAuditService?.getUserPaymentOrders(user?.id);
        const order  = orders?.find(o => o?.id === orderId || o?.razorpay_order_id === orderId);
        if (!order)                            { setError('Payment order not found');  setIsLoading(false); return; }
        if (order?.payment_status !== 'captured') { setError('Payment not completed');    setIsLoading(false); return; }
        setPaymentOrder(order);
        setIsLoading(false);
      } catch (err) {
        console.error('Error loading payment order:', err);
        setError('Failed to load payment details');
        setIsLoading(false);
      }
    };
    if (user?.id) loadPaymentOrder();
  }, [user, location]);

  const handleDownloadInvoice = () => {
    if (!paymentOrder) return;
    try   { invoiceGenerator?.downloadInvoice(paymentOrder, userProfile); toast?.success('Invoice downloaded successfully'); }
    catch { toast?.error('Failed to download invoice'); }
  };

  const handlePrintInvoice = () => {
    if (!paymentOrder) return;
    try   { invoiceGenerator?.printInvoice(paymentOrder, userProfile); }
    catch { toast?.error('Failed to print invoice'); }
  };

  /* ── Loading ── */
  if (isLoading) return (
    <div className="pcs-root">
      <style>{STYLES}</style>
      <RoleBasedHeader userRole="user" pendingAudits={0} />
      <AnnouncementBanner />
      <div className="pcs-center">
        <div>
          <Icon name="Loader2" size={48} color="#004AAD" className="animate-spin" />
          <p>Loading payment details...</p>
        </div>
      </div>
    </div>
  );

  /* ── Error ── */
  if (error || !paymentOrder) return (
    <div className="pcs-root">
      <style>{STYLES}</style>
      <RoleBasedHeader userRole="user" pendingAudits={0} />
      <AnnouncementBanner />
      <div className="pcs-error-wrap">
        <div className="pcs-error-card">
          <div className="pcs-error-alert">
            <Icon name="AlertCircle" size={18} color="#dc2626" style={{ flexShrink: 0 }} />
            <span>{error || 'Payment details not found'}</span>
          </div>
          <button className="pcs-btn-error" onClick={() => navigate('/user-dashboard')}>
            Return to Dashboard
          </button>
        </div>
      </div>
    </div>
  );

  const sym    = paymentOrder?.currency === 'INR' ? '₹' : '$';
  const amount = (paymentOrder?.amount / 100)?.toFixed(2);

  const orderRows = [
    { label: 'Payment Date', value: new Date(paymentOrder?.paid_at)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) },
  ];

  return (
    <div className="pcs-root">
      <style>{STYLES}</style>
      <Helmet>
        <title>Payment Confirmation - myDeckAudit</title>
        <meta name="description" content="Payment confirmation and order details for your premium deck audit" />
      </Helmet>

      <RoleBasedHeader userRole="user" pendingAudits={0} />
      <AnnouncementBanner />

      <main className="pcs-main">
        <div className="pcs-inner">

          {/* Hero */}
          <div className="pcs-hero">
            <div className="pcs-hero-icon">
              <Icon name="CheckCircle2" size={48} color="#16a34a" />
            </div>
            <h1>Payment Successful!</h1>
            <p>Thank you for choosing our premium deck audit service. Your order has been confirmed.</p>
          </div>

          {/* Refund banner */}
          {showRefundBanner && (
            <RefundRequestBanner paymentOrder={paymentOrder} onDismiss={() => setShowRefundBanner(false)} />
          )}

          <div className="pcs-grid">

            {/* ── Left column ── */}
            <div className="pcs-left">

              {/* Order Summary */}
              <div className="pcs-card">
                <div className="pcs-card-title-row">
                  <Icon name="FileCheck2" size={22} color="#004AAD" />
                  <h2 className="pcs-card-title">Order Summary</h2>
                </div>

                {/* Order ID + status */}
                <div className="pcs-row-flex">
                  <div>
                    <p className="pcs-meta-label">Order ID</p>
                    <p className="pcs-order-id">{paymentOrder?.razorpay_order_id?.slice(-12)?.toUpperCase()}</p>
                  </div>
                  <span className="pcs-paid-badge">
                    <Icon name="CheckCircle2" size={13} color="#16a34a" /> Paid
                  </span>
                </div>

                {/* Service */}
                <div className="pcs-row">
                  <p className="pcs-meta-label">Service</p>
                  <p className="pcs-meta-value">Paid Deck Audit — AI + Human Powered</p>
                  <p className="pcs-meta-sub">Comprehensive pitch deck analysis with expert review</p>
                </div>

                {/* Date */}
                <div className="pcs-row">
                  <p className="pcs-meta-label">Payment Date</p>
                  <p className="pcs-meta-value">
                    {new Date(paymentOrder?.paid_at)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>

                {/* Total */}
                <div className="pcs-total-row">
                  <p className="pcs-total-label">Total Amount Paid</p>
                  <p className="pcs-total-amount">{sym}{amount}</p>
                </div>
              </div>

              {/* Transaction Details */}
              <TransactionDetails paymentOrder={paymentOrder} />

              {/* Invoice */}
              <div className="pcs-card">
                <div className="pcs-card-title-row">
                  <Icon name="FileText" size={22} color="#004AAD" />
                  <h2 className="pcs-card-title">Invoice &amp; Receipt</h2>
                </div>
                <p style={{ fontSize: 13, color: 'var(--muted-foreground, #64748b)', margin: '0 0 20px' }}>
                  Download or print your invoice for your records.
                </p>
                <div className="pcs-invoice-btns">
                  <button className="pcs-btn-grad" onClick={handleDownloadInvoice}>
                    <Icon name="Download" size={16} color="#fff" /> Download Invoice
                  </button>
                  <button className="pcs-btn-outline" onClick={handlePrintInvoice}>
                    <Icon name="Printer" size={16} /> Print Invoice
                  </button>
                </div>
              </div>
            </div>

            {/* ── Right column ── */}
            <div className="pcs-right">

              {/* Email confirmation */}
              <div className="pcs-email-card">
                <div className="pcs-email-inner">
                  <Icon name="Mail" size={22} color="#004AAD" style={{ flexShrink: 0 }} />
                  <div>
                    <h3>Email Confirmation</h3>
                    <p className="sub">Receipt sent to:</p>
                    <p className="addr">{paymentOrder?.customer_email}</p>
                  </div>
                </div>
                <p className="pcs-email-note">
                  You'll receive timeline updates and notifications about your audit progress at this email address.
                </p>
              </div>

              {/* Audit Timeline */}
              <AuditTimeline submissionId={paymentOrder?.submission_id} />

              {/* Quick Actions */}
              <div className="pcs-actions-card">
                <h3>Quick Actions</h3>
                <div className="pcs-actions-list">
                  <button className="pcs-btn-grad-sm" onClick={() => navigate('/user-dashboard')}>
                    <Icon name="LayoutDashboard" size={16} color="#fff" /> Return to Dashboard
                  </button>
                  <button className="pcs-btn-outline-sm" onClick={() => navigate('/initial-form-upload-screen')}>
                    <Icon name="Upload" size={16} /> Upload Another Deck
                  </button>
                </div>
              </div>

              {/* Trust */}
              <div className="pcs-trust-card">
                <div className="pcs-trust-row">
                  <Icon name="Shield" size={18} color="#16a34a" />
                  <h3>Secure Payment</h3>
                </div>
                <p className="pcs-trust-body">
                  Your payment was processed securely through Razorpay with industry-standard encryption.
                </p>
                <div className="pcs-trust-support">
                  <Icon name="HelpCircle" size={14} color="var(--muted-foreground, #94a3b8)" />
                  <span>Need help? Contact support@mydeckaudit.com</span>
                </div>
              </div>

            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default PaymentConfirmationScreen;